import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, ChevronDown, ChevronRight, ArrowUp, ArrowDown, FlaskConical } from 'lucide-react';

// The rate card behind the "Additional charge" picker on an order.
// A charge has one generic rate, and any customer can carry its own, which wins.

const BLANK = { code: '', label: '', unit: 'flat', default_amount: '', notes: '' };
const BLANK_RULE = { attribute_code: '', match_value: '', qty_mode: 'fixed', qty_fixed: 1, qty_code: '', qty_times_code: '' };

// Rules turn a customer's answers into a charge on their order. Since v1.34.0
// this happens as they order: the price they are quoted at add-to-cart already
// includes it, and the charge is written onto the order when they place it.
function RulesEditor({ chargeId, unit, qtyModes, codes }) {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [targets, setTargets] = useState({ products: [], sets: [] });

  useEffect(() => {
    api.get('/api/charge-types/scope-targets')
      .then(r => setTargets({ products: r.data.products || [], sets: r.data.sets || [] }))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get(`/api/charge-types/${chargeId}/rules`)
      .then(r => setRules(r.data.rules))
      .catch(() => setRules([]));
  }, [chargeId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (draft.id) {
        await api.put(`/api/charge-types/${chargeId}/rules/${draft.id}`, draft);
        await api.put(`/api/charge-types/${chargeId}/rules/${draft.id}/scopes`, { scopes: draft.scopes || [] });
      } else {
        await api.post(`/api/charge-types/${chargeId}/rules`, draft);
      }
      setDraft(null); load();
      toast.success('Rule saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save the rule'); }
  };

  const remove = async (ruleId) => {
    try { await api.delete(`/api/charge-types/${chargeId}/rules/${ruleId}`); load(); toast.success('Rule removed'); }
    catch (e) { toast.error('Could not remove the rule'); }
  };

  // Show the question's LABEL where one exists — a bare code tells you nothing
  // about whether it is the question you meant.
  const describe = (r) => {
    const name = (code, label) => label ? `${label}` : code;
    const trigger = r.attribute_code
      ? `when ${name(r.attribute_code, r.attribute_label)}${r.match_value ? ` is "${r.match_value}"` : ' is answered'}`
      : 'on every order';
    const qty = r.qty_mode === 'fixed' ? `${r.qty_fixed}`
      : r.qty_mode === 'line_qty' ? 'the item quantity'
      : r.qty_mode === 'line_qty_m' ? 'the item quantity ÷ 1000'
      : r.qty_mode === 'plates' ? 'sides printed + inks that are not black'
      : r.qty_mode === 'color_ink_m' ? 'the item quantity ÷ 1000, and nothing when every ink is black'
      : `${name(r.qty_code, r.qty_label)}${r.qty_times_code ? ` × ${name(r.qty_times_code, r.qty_times_label)}` : ''}`;
    return unit === 'flat' ? `Charge ${trigger}` : `Charge ${trigger}, quantity = ${qty}`;
  };

  const CodeInput = ({ value, onChange, placeholder }) => (
    <>
      <input className="form-input" list="ttc-question-codes" value={value || ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} />
    </>
  );

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
      <datalist id="ttc-question-codes">
        {codes.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
      </datalist>

      <div className="form-label">Charge this automatically</div>
      <p className="form-hint" style={{ marginBottom: 8 }}>
        Based on the answers captured by the Question Builder. The customer sees this in the
        price as they answer, and it is charged when they place the order — so test a rule
        below before saving it.
      </p>

      {rules === null ? <div className="text-sm text-muted">Loading…</div> : (
        <>
          {rules.length === 0 && !draft && (
            <div className="text-sm text-muted" style={{ marginBottom: 8 }}>
              No rules — this charge is only ever added by hand on an order.
            </div>
          )}
          {rules.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                {describe(r)}{!r.active && <span className="text-muted"> (off)</span>}
                {r.scopes?.length > 0 && (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    only on: {r.scopes.map(x => x.label).join(', ')}
                  </div>
                )}
                {r.unknown_codes?.length > 0 && (
                  <div style={{ color: 'var(--brand-red)', fontSize: 12 }}>
                    No question uses {r.unknown_codes.map(c => `"${c}"`).join(', ')} — this rule can never fire.
                  </div>
                )}
              </span>
              <button className="link-btn" onClick={() => setDraft({ ...r })}>edit</button>
              <button className="link-btn" style={{ color: 'var(--brand-red)' }} onClick={() => remove(r.id)}>remove</button>
            </div>
          ))}

          {draft ? (
            <div style={{ marginTop: 10, padding: 10, background: 'var(--gray-50)', borderRadius: 6 }}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">When this question…</label>
                  <CodeInput value={draft.attribute_code} placeholder="any order"
                    onChange={v => setDraft(d => ({ ...d, attribute_code: v }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">…has this answer</label>
                  <input className="form-input" value={draft.match_value || ''} placeholder="any answer except No"
                    onChange={e => setDraft(d => ({ ...d, match_value: e.target.value }))} />
                </div>
              </div>

              {unit !== 'flat' && (
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Quantity comes from</label>
                    <select className="form-select" value={draft.qty_mode}
                      onChange={e => setDraft(d => ({ ...d, qty_mode: e.target.value }))}>
                      {qtyModes.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  {draft.qty_mode === 'fixed' && (
                    <div className="form-group" style={{ flex: '0 0 120px' }}>
                      <label className="form-label">How many</label>
                      <input className="form-input" type="number" step="any" min="0" value={draft.qty_fixed ?? 1}
                        onChange={e => setDraft(d => ({ ...d, qty_fixed: e.target.value }))} />
                    </div>
                  )}
                  {draft.qty_mode === 'answer' && (
                    <>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Answer to this question</label>
                        <CodeInput value={draft.qty_code} placeholder="e.g. sides"
                          onChange={v => setDraft(d => ({ ...d, qty_code: v }))} />
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">…multiplied by (optional)</label>
                        <CodeInput value={draft.qty_times_code} placeholder="e.g. ink_colors"
                          onChange={v => setDraft(d => ({ ...d, qty_times_code: v }))} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {draft.id && (
                <div style={{ marginBottom: 10 }}>
                  <label className="form-label">Only charge on these items</label>
                  <p className="form-hint" style={{ marginTop: 0 }}>
                    Leave empty to charge on every item. Use this when a question is asked widely but
                    the charge applies to only some items — a barcode question on a size whose price
                    already includes the barcode, for instance.
                  </p>
                  <select className="form-select" multiple size={6} value={(draft.scopes || []).map(x => `product:${x.scope_id}`)}
                    onChange={e => setDraft(d => ({ ...d, scopes: Array.from(e.target.selectedOptions)
                      .map(o => ({ scope_type: 'product', scope_id: Number(o.value.split(':')[1]) })) }))}>
                    {targets.products.map(p => (
                      <option key={p.id} value={`product:${p.id}`}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={save}>Save rule</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setDraft(null)}>Cancel</button>
                {draft.id && (
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginLeft: 8 }}>
                    <input type="checkbox" checked={draft.active !== false}
                      onChange={e => setDraft(d => ({ ...d, active: e.target.checked }))} />
                    On
                  </label>
                )}
              </div>
            </div>
          ) : (
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
              onClick={() => setDraft({ ...BLANK_RULE })}><Plus size={13} /> Add a rule</button>
          )}
        </>
      )}
    </div>
  );
}

function money(v) {
  return v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`;
}

// One charge in the list: summary row, editor, and its per-customer rates.
function ChargeRow({ charge, units, qtyModes, codes, customers, expanded, onToggle, onSaved, onDeleted, onMove }) {
  const [form, setForm] = useState(charge);
  const [rates, setRates] = useState(null);
  const [adding, setAdding] = useState({ customer_id: '', amount: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(charge); }, [charge]);

  const loadRates = useCallback(() => {
    api.get(`/api/charge-types/${charge.id}/rates`)
      .then(r => setRates(r.data.rates))
      .catch(() => setRates([]));
  }, [charge.id]);

  useEffect(() => { if (expanded && rates === null) loadRates(); }, [expanded, rates, loadRates]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/api/charge-types/${charge.id}`, form);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/charge-types/${charge.id}`);
      toast.success('Deleted');
      onDeleted();
    } catch (e) {
      // 409 means it is on real orders — the server tells us to deactivate.
      toast.error(e.response?.data?.error || 'Could not delete');
    }
  };

  const setRate = async (customerId, amount) => {
    if (!customerId) return toast.error('Choose a customer');
    if (amount === '' || isNaN(Number(amount))) return toast.error('Enter an amount');
    try {
      await api.put(`/api/charge-types/${charge.id}/rates/${customerId}`, { amount: Number(amount) });
      setAdding({ customer_id: '', amount: '' });
      loadRates();
      toast.success('Customer rate saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save the rate'); }
  };

  const clearRate = async (customerId) => {
    try {
      await api.delete(`/api/charge-types/${charge.id}/rates/${customerId}`);
      loadRates();
      toast.success('Back to the generic rate');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not remove the rate'); }
  };

  const unused = customers.filter(c => !(rates || []).some(r => r.customer_id === c.id));

  return (
    <div style={{ borderBottom: '1px solid var(--gray-200)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px' }}>
        <button className="link-btn" onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, textAlign: 'left' }}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="fw-600">{charge.label}</span>
          {!charge.active && <span className="text-sm text-muted">(inactive)</span>}
        </button>
        <span className="text-sm text-muted" style={{ flex: '0 0 140px' }}>{charge.unit_label}</span>
        <span className="text-sm" style={{ flex: '0 0 90px', textAlign: 'right' }}>{money(charge.default_amount)}</span>
        <span style={{ display: 'flex', gap: 2 }}>
          <button className="link-btn" title="Move up" onClick={() => onMove('up')}><ArrowUp size={13} /></button>
          <button className="link-btn" title="Move down" onClick={() => onMove('down')}><ArrowDown size={13} /></button>
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '4px 4px 16px 24px' }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Name</label>
              <input className="form-input" value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Charged</label>
              <select className="form-select" value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: '0 0 150px' }}>
              <label className="form-label">Generic rate ($)</label>
              <input className="form-input" type="number" step="0.01" value={form.default_amount ?? ''}
                placeholder="none"
                onChange={e => setForm(f => ({ ...f, default_amount: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Note (optional)</label>
            <input className="form-input" value={form.notes || ''}
              placeholder="e.g. per side — double-sided is two"
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
              <Save size={13} /> Save
            </button>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={!!form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)', marginLeft: 'auto' }}
              onClick={remove}><Trash2 size={13} /> Delete</button>
          </div>

          <div className="form-label">Customer rates</div>
          <p className="form-hint" style={{ marginBottom: 8 }}>
            A customer listed here is charged its own rate. Everyone else gets the generic rate
            {form.default_amount === '' || form.default_amount == null
              ? ' — and there isn’t one, so this charge has to be priced by hand on the order.'
              : `, ${money(form.default_amount)}.`}
          </p>

          {rates === null ? <div className="text-sm text-muted">Loading…</div> : (
            <>
              {rates.length === 0 && <div className="text-sm text-muted" style={{ marginBottom: 8 }}>No customer rates — everyone is on the generic rate.</div>}
              {rates.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                  <span style={{ flex: 1, fontSize: 13 }}>{r.customer_name}</span>
                  <input className="form-input" type="number" step="0.01" defaultValue={r.amount}
                    style={{ flex: '0 0 120px' }}
                    onBlur={e => { if (Number(e.target.value) !== r.amount) setRate(r.customer_id, e.target.value); }} />
                  <button className="link-btn" style={{ color: 'var(--brand-red)' }}
                    onClick={() => clearRate(r.customer_id)}>remove</button>
                </div>
              ))}

              {unused.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 8 }}>
                  <select className="form-select" style={{ flex: 1 }} value={adding.customer_id}
                    onChange={e => setAdding(a => ({ ...a, customer_id: e.target.value }))}>
                    <option value="">Add a customer rate…</option>
                    {unused.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input className="form-input" type="number" step="0.01" placeholder="Amount"
                    style={{ flex: '0 0 120px' }} value={adding.amount}
                    onChange={e => setAdding(a => ({ ...a, amount: e.target.value }))} />
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setRate(adding.customer_id, adding.amount)}>Add</button>
                </div>
              )}
            </>
          )}

          <RulesEditor chargeId={charge.id} unit={form.unit} qtyModes={qtyModes} codes={codes} />
        </div>
      )}
    </div>
  );
}

// Try the rules against sample answers before they touch a real order. Rules
// decide what a customer is quoted, so this is where a mistake should surface.
function RuleTester({ customers, codes }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [productId, setProductId] = useState('');
  const [targets, setTargets] = useState({ products: [] });

  useEffect(() => {
    api.get('/api/charge-types/scope-targets')
      .then(r => setTargets({ products: r.data.products || [] }))
      .catch(() => {});
  }, []);
  const [quantity, setQuantity] = useState(100000);
  const [rows, setRows] = useState([{ code: '', value: '' }]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const answers = {};
    rows.forEach(r => { if (r.code.trim()) answers[r.code.trim()] = r.value; });
    setBusy(true);
    try {
      const r = await api.post('/api/charge-types/test', {
        answers, quantity: Number(quantity), customer_id: customerId || undefined,
        product_id: productId || undefined,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not run the test');
      setResult(null);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
        <FlaskConical size={13} /> Test the rules
      </button>
    );
  }

  return (
    <div>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Enter the answers a customer might give and see what the rules would charge.
        Nothing is saved and no order is created.
      </p>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">As this customer</label>
          <select className="form-select" value={customerId} onChange={e => setCustomerId(e.target.value)}>
            <option value="">Generic rates</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">As this item</label>
          <select className="form-select" value={productId} onChange={e => setProductId(e.target.value)}>
            <option value="">Any item</option>
            {targets.products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ flex: '0 0 160px' }}>
          <label className="form-label">Item quantity</label>
          <input className="form-input" type="number" min="1" value={quantity}
            onChange={e => setQuantity(e.target.value)} />
        </div>
      </div>

      <div className="form-label">Answers</div>
      {rows.map((r, i) => (
        <div className="form-row" key={i} style={{ alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 8 }}>
            <input className="form-input" list="ttc-question-codes" placeholder="question code"
              value={r.code} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 8 }}>
            <input className="form-input" placeholder="answer"
              value={r.value} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
          </div>
          <div className="form-group" style={{ flex: '0 0 auto', marginBottom: 8 }}>
            <button className="link-btn" style={{ color: 'var(--brand-red)' }}
              onClick={() => setRows(rs => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs)}>remove</button>
          </div>
        </div>
      ))}
      <datalist id="ttc-question-codes">
        {codes.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
      </datalist>

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setRows(rs => [...rs, { code: '', value: '' }])}>
          <Plus size={13} /> Another answer
        </button>
        <button className="btn btn-primary btn-sm" onClick={run} disabled={busy}>
          {busy ? 'Running…' : 'Run'}
        </button>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
          onClick={() => { setOpen(false); setResult(null); }}>Close</button>
      </div>

      {result && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
          {result.charges.length === 0
            ? <div className="text-sm text-muted">No charges — nothing in these answers triggers a rule.</div>
            : (
              <>
                {result.charges.map(c => (
                  <div key={c.charge_type_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                    <span style={{ fontSize: 13 }}>
                      <strong>{c.label}</strong> <span className="text-muted">— {c.why}</span>
                    </span>
                    <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {c.amount == null
                        ? <em style={{ color: 'var(--brand-red)' }}>{c.needs_attention || 'not priced'}</em>
                        : `$${Number(c.amount).toFixed(2)}`}
                    </span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--gray-200)', marginTop: 6, paddingTop: 6, fontWeight: 600 }}>
                  <span>Charges on this order</span><span>${Number(result.charges_total).toFixed(2)}</span>
                </div>
              </>
            )}
          {result.note && <p className="form-hint" style={{ color: 'var(--brand-red)' }}>{result.note}</p>}

          {result.skipped.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary className="text-sm text-muted">{result.skipped.length} rule(s) did not fire</summary>
              {result.skipped.map((s, i) => (
                <div key={i} style={{ fontSize: 12, padding: '2px 0' }} className="text-muted">
                  <strong>{s.label}</strong> — {s.why}
                </div>
              ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChargeCatalogPage() {
  const [charges, setCharges] = useState([]);
  const [units, setUnits] = useState([]);
  const [qtyModes, setQtyModes] = useState([]);
  const [codes, setCodes] = useState([]);
  const [problems, setProblems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK);

  const load = useCallback(() => {
    return api.get('/api/charge-types?include_inactive=1')
      .then(r => setCharges(r.data.charge_types))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/api/charge-types/units'),
      api.get('/api/customers'),
      api.get('/api/charge-types/question-codes'),
      api.get('/api/charge-types/rule-problems'),
    ]).then(([u, c, q, pr]) => {
      setProblems(pr.data.problems || []);
      setUnits(u.data.units);
      setQtyModes(u.data.qty_modes || []);
      setCustomers(c.data.customers || []);
      setCodes(q.data.codes || []);
    }).catch(() => {});
    load();
  }, [load]);

  const create = async () => {
    if (!draft.label.trim()) return toast.error('Give the charge a name');
    try {
      const r = await api.post('/api/charge-types', draft);
      setDraft(BLANK); setAdding(false);
      await load();
      setExpandedId(r.data.id);
      toast.success('Charge added');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add it'); }
  };

  const move = async (id, direction) => {
    try {
      await api.post(`/api/charge-types/${id}/move`, { direction });
      load();
    } catch (e) { toast.error('Could not reorder'); }
  };

  if (loading) return (<><div className="topbar"><h2>Charge Catalog</h2></div><div className="page"><div className="loading">Loading…</div></div></>);

  return (
    <>
      <div className="topbar"><h2>Charge Catalog</h2></div>
      <div className="page">
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><h3>Charges staff can add to an order</h3></div>

          {problems.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }}>
              <strong>{problems.length} rule{problems.length > 1 ? 's' : ''} can never fire.</strong>
              {problems.map(p => (
                <div key={p.rule_id} style={{ fontSize: 13, marginTop: 4 }}>
                  {p.charge_label} — {p.why}
                </div>
              ))}
              <div className="form-hint" style={{ marginTop: 6 }}>
                Usually a question was renamed or the code was mistyped. Until it is fixed the
                charge is never billed.
              </div>
            </div>
          )}
          <p className="form-hint" style={{ marginBottom: 12 }}>
            Plate charges, ink, multi-copy, stock color changes — anything charged over and above
            the items. Set the rate once here and staff pick it on the order instead of retyping it.
            Editing a rate here changes what future orders are charged; orders already placed keep
            what they were priced at.
          </p>

          {charges.length === 0 && (
            <div className="text-sm text-muted" style={{ padding: '8px 0' }}>
              Nothing in the catalog yet. Staff can still type a charge by hand on any order.
            </div>
          )}

          {charges.length > 0 && (
            <div style={{ display: 'flex', gap: 12, padding: '0 4px 6px', borderBottom: '2px solid var(--gray-200)' }}>
              <span className="form-label" style={{ flex: 1, marginBottom: 0 }}>Charge</span>
              <span className="form-label" style={{ flex: '0 0 140px', marginBottom: 0 }}>Charged</span>
              <span className="form-label" style={{ flex: '0 0 90px', marginBottom: 0, textAlign: 'right' }}>Generic</span>
              <span style={{ flex: '0 0 46px' }} />
            </div>
          )}

          {charges.map(c => (
            <ChargeRow key={c.id} charge={c} units={units} qtyModes={qtyModes} codes={codes} customers={customers}
              expanded={expandedId === c.id}
              onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              onSaved={load}
              onDeleted={() => { setExpandedId(null); load(); }}
              onMove={(dir) => move(c.id, dir)} />
          ))}

          {!adding ? (
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 14 }}
              onClick={() => setAdding(true)}><Plus size={13} /> Add a charge</button>
          ) : (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--gray-200)' }}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Name</label>
                  <input className="form-input" autoFocus value={draft.label}
                    placeholder="e.g. New plate charge"
                    onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Charged</label>
                  <select className="form-select" value={draft.unit}
                    onChange={e => setDraft(d => ({ ...d, unit: e.target.value }))}>
                    {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: '0 0 150px' }}>
                  <label className="form-label">Generic rate ($)</label>
                  <input className="form-input" type="number" step="0.01" value={draft.default_amount}
                    placeholder="optional"
                    onChange={e => setDraft(d => ({ ...d, default_amount: e.target.value }))} />
                </div>
              </div>
              <p className="form-hint" style={{ marginBottom: 10 }}>
                Leave the generic rate blank if this charge is always negotiated per customer.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={create}>Add</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setDraft(BLANK); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header"><h3>Try a rule before it goes live</h3></div>
          <RuleTester customers={customers} codes={codes} />
        </div>
      </div>
    </>
  );
}
